import React, { createContext, useContext, useState } from 'react';
import { lgData, lgPlayerId } from '../../utils/lgPlayer';
import { getDeviceOperationalState } from '../../utils/screenActivity';
import { inputEvidenceFresh } from '../../utils/screenActivity';
import axios from 'axios';
import LgPlayerPanel from '../Controls/LgPlayerPanel';
import LgDeviceAppEditor from '../Settings/LgDeviceAppEditor';

export const LgConnectionsContext = createContext({});
export function lgConnectionStatus(device, target, playerState, relay, now = Date.now()) {
  const linked = lgPlayerId(device);
  const installed = lgData(device).remoteTarget?.appInventory?.launchable?.apps?.some((app) =>
    ['com.suitestream.player', 'com.suitestream.player.debug'].includes(app.nativeId) && app.launchable !== false && app.hidden !== true);
  const last = Date.parse(target?.lastConnectedAt || '');
  const recent = Number.isFinite(last) && last <= now + 2000 && now - last <= 60000;
  const operational = getDeviceOperationalState(playerState, now);
  const relayLive = relay?.liveState?.native?.connected;
  return {
    ip: !target ? 'Status unavailable' : !target.paired ? 'Not paired' : target.lastError ? 'Unavailable' : recent ? 'Connected · recently seen' : 'Paired · not recently checked',
    player: linked ? operational.player.connected ? 'Online' : operational.player.label.includes('unknown') ? 'Status unknown' : 'Offline'
      : installed ? 'Installed — not connected' : 'Not linked',
    relay: relay ? `${relay.name || `Android ${relay.id}`} · ${relayLive === true ? 'Connected' : relayLive === false ? 'Offline' : 'Status unknown'}` : 'Not reported',
    lastSeen: Number.isFinite(last) ? new Date(last).toLocaleString() : 'Not reported',
  };
}
export default function LgTvStatusCard({ device, actions, onSelect, siteId, onOpenDevicePairing }) {
  const { lgTargets = {}, liveStates = {}, nativeNodes = {}, tvInputReports = {} } = useContext(LgConnectionsContext);
  const target = lgTargets[device.id];
  const relayId = target?.lastBridgeDeviceId || target?.controlledBy;
  const status = lgConnectionStatus(device, target, liveStates[lgPlayerId(device)], nativeNodes[relayId]);
  const [settings, setSettings] = useState(false);
  const [apps, setApps] = useState(false);
  const [launch, setLaunch] = useState('');
  const [launching, setLaunching] = useState(false);
  const report = tvInputReports[device.id];
  const activity = inputEvidenceFresh(report) && report?.activeSource?.available === true
    ? report.activeSource.label || (report.activeSource.port ? `HDMI ${report.activeSource.port}` : null) : null;
  const installed = lgData(device).remoteTarget?.appInventory?.launchable?.apps?.some((app) =>
    ['com.suitestream.player', 'com.suitestream.player.debug'].includes(app.nativeId) && app.launchable !== false && app.hidden !== true);
  const openPlayer = async () => {
    if (launching) return;
    setLaunching(true); setLaunch('');
    try {
      const result = await axios.post(`/api/lg-player/${device.id}/open`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 20000,
      });
      setLaunch(result.data.message);
    } catch (error) { setLaunch(error.response?.data?.message || 'Could not open the Player. Review TV pairing and the Android relay.'); }
    finally { setLaunching(false); }
  };
  return <div className="topology-live__device" aria-label={`LG TV ${device.name}`}>
    <button type="button" className="topology-live__device-select" onClick={() => onSelect?.(device.id)}><span className="topology-live__device-role">LG Television · IP Control{lgPlayerId(device) ? ' + Player' : ''}</span><strong>{device.name}</strong></button>
    <div className="topology-live__signals">
      <span className="topology-live__signal">IP control: {status.ip}</span>
      <span className="topology-live__signal">Player: {status.player}</span>
      <span className="topology-live__signal">Android relay: {status.relay}</span>
      <span className="topology-live__signal">TV last contact: {status.lastSeen}</span>
      <span className="topology-live__signal">TV activity: {activity || 'Not recently reported'}</span>
    </div>
    {target?.lastError && <p role="status">{target.lastError}</p>}
    <div className="topology-live__device-actions">{actions}
      <button type="button" className="topology-live__activate-source" disabled={launching || !installed || !target?.paired} onClick={openPlayer}>Launch Suitestream Player</button>
      <button type="button" className="topology-live__activate-source" onClick={() => setSettings(true)}>Player settings &amp; connections</button>
      <button type="button" className="topology-live__activate-source" onClick={() => setApps(true)}>Manage TV Apps</button>
      <button type="button" className="topology-live__activate-source" onClick={() => onOpenDevicePairing?.(device.id)}>Rename / TV pairing</button>
    </div>
    {launch && <p role="status">{launch}</p>}
    {settings && <div className="modal lg-device-app-editor" role="dialog" aria-modal="true" aria-label="LG Player settings"><div className="modal-content settings-modal-content settings-modal-content--wide">
      <LgPlayerPanel key={device.id} device={device} siteId={siteId} paired={target?.paired === true} />
      <button type="button" className="settings-modal-button secondary" onClick={() => setSettings(false)}>Close</button>
    </div></div>}
    {apps && <LgDeviceAppEditor key={device.id} device={device} siteId={siteId} onClose={() => setApps(false)} />}
  </div>;
}
